import React, { useEffect } from 'react';
import dayjs from 'dayjs';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { initialState as customerTransferInitialState } from '~redux/slices/customerTransfer';

import SearchAndFilterBar, { TSearchAndFilterBarProps } from '~components/molecules/SearchAndFilterBar';

import { MetalTypeEnum, SilverGoldTypeEnum, TransactionActionEnum, TransactionStatusEnum } from '~utils/enums';

import * as Styles from './CustomerTransferSearchAndFilterBar.module.scss';
import { TGetSalesReportTransactionListingFilters } from '~types/feat/report';
import { TGetCustomerTransferListingFilters } from '~types/feat/customerTransfer';

const CustomerTransferSearchAndFilterBar = () => {
    const dispatch = useAppDispatch();
    const filters = useAppSelector(state => state.customerTransfer.customerTransferListing.filters);
    const { allBranchAccess, branchCode } = useAppSelector(state => state.auth.userDetails);
    const initialFilters = customerTransferInitialState.customerTransferListing.filters;
    const branchesList = useAppSelector(state => state.branch.allBranches);
    const productList = useAppSelector((state) => state.gold.allProducts);

    const setFilters = (inputFilters: TGetCustomerTransferListingFilters) => dispatch(combinedActions.customerTransferSetCustomerTransferListingFilters(inputFilters));
    const handleSearchClick = () => {
        dispatch(combinedActions.customerTransferGetCustomerTransferListingAttempt());
    };
    const handleCancelClick = () => {
        const resetFilters = {
            ...initialFilters,
            branchName: !allBranchAccess
                ? (branchesList.find(branch => branch.branchCode === branchCode)?.branchName || '')
                : '',
        };

        setFilters(resetFilters);
        dispatch(combinedActions.customerTransferGetCustomerTransferListingAttempt());
    };

    useEffect(() => {
        if (branchesList.length > 0 && !allBranchAccess) {
            setFilters({
                ...filters,
                branchName: branchesList.find((branch) => branch.branchCode === branchCode)?.branchName || '',
            });
        }
    }, [branchesList]);

    const massagedBranchesListOption = () => {
        if (!branchesList.length) return [];

        return branchesList.map((branch) => ({
            label: branch.branchName,
            value: branch.branchCode,
        }));
    };

    const isSilver = filters.metalType === MetalTypeEnum[MetalTypeEnum.Silver];

    const massagedProductsListOption = () => {
        if (!productList.length) return [];
        return productList.map((product) => ({
            label: product.productName,
            value: product.productId,
        }));
    };

    const fields: TSearchAndFilterBarProps['fields'] = [
        {
            label: 'Sender Name:',
            type: 'input' as const,
            inputParams: {
                value: filters.senderName || '',
                setValue: (event) => setFilters({ ...filters, senderName: event.target.value }),
                onKeyDown: (event) => { if (event.key === 'Enter') handleSearchClick(); },
                className: Styles.input,
            },
        },
        {
            label: 'Sender ID No:',
            type: 'input' as const,
            inputParams: {
                value: filters.senderIdNo || '',
                setValue: (event) => setFilters({ ...filters, senderIdNo: event.target.value }),
                onKeyDown: (event) => { if (event.key === 'Enter') handleSearchClick(); },
                className: Styles.input,
            },
        },
        {
            label: 'Receiver Name:',
            type: 'input' as const,
            inputParams: {
                value: filters.receiverName || '',
                setValue: (event) => setFilters({ ...filters, receiverName: event.target.value }),
                onKeyDown: (event) => { if (event.key === 'Enter') handleSearchClick(); },
                className: Styles.input,
            },
        },
        {
            label: 'Receiver ID No:',
            type: 'input' as const,
            inputParams: {
                value: filters.receiverIdNo || '',
                setValue: (event) => setFilters({ ...filters, receiverIdNo: event.target.value }),
                onKeyDown: (event) => { if (event.key === 'Enter') handleSearchClick(); },
                className: Styles.input,
            },
        },
        {
            label: 'Date From:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateFrom,
                setDate: (date: string) => {
                    if (date > filters?.dateTo) {
                        setFilters({ ...filters, dateFrom: date, dateTo: '' });
                    } else setFilters({ ...filters, dateFrom: date });
                },
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsStart: true,
                maxDate: dayjs().toISOString(),
            },
        },
        {
            label: 'Date To:',
            type: 'datepicker' as const,
            datepickerParams: {
                date: filters.dateTo,
                minDate: filters.dateFrom,
                setDate: (date: string) => setFilters({ ...filters, dateTo: date }),
                startDate: filters.dateFrom,
                endDate: filters.dateTo,
                selectsEnd: true,
            },
        },
        {
            label: 'Branch:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: massagedBranchesListOption(),
                value: filters.branchName || '',
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({
                    ...filters,
                    branchName: event.target.value,
                }),
                optionSelected: !!branchesList.find((branch) => branch.branchName === filters.branchName),
                noOptionsMessage: 'No branches found',
                className: Styles.input,
                disabled: !allBranchAccess,
            },
        },
        {
            label: 'Status:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: TransactionStatusEnum[TransactionStatusEnum.Approved],
                        value: TransactionStatusEnum.Approved,
                    },
                    {
                        label: TransactionStatusEnum[TransactionStatusEnum.Pending],
                        value: TransactionStatusEnum.Pending,
                    },
                    {
                        label: TransactionStatusEnum[TransactionStatusEnum.Rejected],
                        value: TransactionStatusEnum.Rejected,
                    },
                ],
                value: filters.status,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, status: event.target.value }),
                optionSelected: !!TransactionStatusEnum[filters.status as keyof typeof TransactionStatusEnum],
                noOptionsMessage: 'No status found',
                className: Styles.input,
            },
        },
        {
            label: 'Metal Type:',
            type: 'dropdown' as const,
            dropdownParams: {
                options: [
                    {
                        label: MetalTypeEnum[MetalTypeEnum.Gold],
                        value: MetalTypeEnum[MetalTypeEnum.Gold],
                    },
                    {
                        label: MetalTypeEnum[MetalTypeEnum.Silver],
                        value: MetalTypeEnum[MetalTypeEnum.Silver],
                    },
                ],
                value: filters.metalType,
                setValue: (event: React.ChangeEvent<HTMLInputElement>) =>
                    setFilters({
                        ...filters,
                        metalType: event.target.value,
                    }),
                optionSelected: !!filters.metalType,
                noOptionsMessage: 'No metal type found',
            },
        },
    ];

    return (
        <SearchAndFilterBar
            fields={fields}
            searchOnClick={handleSearchClick}
            cancelOnClick={handleCancelClick}
            containerClassName={Styles['content-container']}
            gridColumns={4}
        />
    );
};

export default CustomerTransferSearchAndFilterBar;
